import { yupResolver } from "@hookform/resolvers/yup";
import { useCallback, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import Button from "../Button";
import FormDateInput from "../FormDateInput";
import FormTextInput from "../FormTextInput";
import Typography from "../Typography";
import {
    LinkEvidenceFormValues,
    defaultLinkEvidenceFormValues,
    linkEvidenceFormValidationSchema,
} from "./CreateEvidenceFormValues";

type LinkEvidenceFormProps = {
    evidenceTypeName: string;
    emissionsSourceName?: string;
    isLoading?: boolean;
    isOpen?: boolean;
    initialValues?: Partial<LinkEvidenceFormValues>;
    onSubmit: (values: LinkEvidenceFormValues) => void;
    onCancel: () => void;
};

const CreateEvidenceForm = ({
    evidenceTypeName,
    emissionsSourceName,
    isLoading,
    isOpen,
    initialValues,
    onSubmit,
    onCancel,
}: LinkEvidenceFormProps) => {
    const methods = useForm<LinkEvidenceFormValues>({
        mode: "onSubmit",
        resolver: yupResolver(linkEvidenceFormValidationSchema),
        defaultValues: { ...defaultLinkEvidenceFormValues, ...initialValues },
    });

    const validFromDate = methods.watch("validFromDate");
    const validToDate = methods.watch("validToDate");

    const handleCloseLinkEvidenceForm = useCallback(() => {
        onCancel();
        methods.reset();
    }, [methods, onCancel]);

    useEffect(() => {
        // Resets form if/when parent dialog is closed
        if (typeof isOpen === "boolean" && !isOpen) {
            handleCloseLinkEvidenceForm();
        }
    }, [isOpen, handleCloseLinkEvidenceForm]);

    return (
        <form onSubmit={methods.handleSubmit(onSubmit)}>
            <div className="mb-8">
                <Typography variant="body-large" component="p">
                    <b>Evidence type:</b> {evidenceTypeName}
                </Typography>
                {emissionsSourceName && (
                    <Typography variant="body-large" component="p">
                        <b>Emissions source:</b> {emissionsSourceName}
                    </Typography>
                )}
            </div>
            <div className="space-y-4">
                <FormTextInput
                    required
                    label="Document URL"
                    placeholder="Enter document URL"
                    {...methods.register("documentUrl")}
                    error={methods.formState.errors.documentUrl}
                />
                <div className="grid grid-cols-2 gap-6">
                    <Controller
                        name="validFromDate"
                        control={methods.control}
                        render={({ field: { onChange, value } }) => (
                            <FormDateInput
                                required
                                id="valid-from-date"
                                label="Valid from"
                                helperText="DD/MM/YYYY"
                                onChange={onChange}
                                value={value}
                                maxDate={validToDate ?? undefined}
                                error={methods.formState.errors.validFromDate}
                            />
                        )}
                    />
                    <Controller
                        name="validToDate"
                        control={methods.control}
                        render={({ field: { onChange, value } }) => (
                            <FormDateInput
                                required
                                id="valid-to-date"
                                label="Valid to"
                                helperText="DD/MM/YYYY"
                                onChange={onChange}
                                value={value}
                                minDate={validFromDate ?? undefined}
                                error={methods.formState.errors.validToDate}
                            />
                        )}
                    />
                </div>
            </div>
            <div className="flex justify-end gap-4 pt-7">
                <Button
                    variant="transparent"
                    type="button"
                    disabled={isLoading}
                    onClick={onCancel}
                >
                    Cancel
                </Button>
                <Button loading={isLoading}>Link evidence</Button>
            </div>
        </form>
    );
};

export default CreateEvidenceForm;
